/**
 * h6-animation-loader.js
 *
 * 只全局加载本文件。
 *
 * 它会在需要的时候按顺序加载：
 * GSAP
 * -> ScrollTrigger
 * -> 对应动画模块
 *
 * Background / Block 已完全独立：
 * - data-h6-scroll-scene
 * - data-h6-scroll-background
 * - data-h6-scroll-block
 *
 * Video Scrub：
 * - data-h6-video-scrub
 *
 * Video Scrub 的 JS 直接写在 video-scrub.liquid 中，
 * 本 Loader 只负责在接近视口时确保 GSAP / ScrollTrigger 可用，
 * 然后调用 window.H6VideoScrub.init(element)。
 *
 * 任意一种都可以单独触发公共模块加载。
 */

(function () {
  "use strict";

  if (window.H6AnimationLoader) {
    return;
  }

  const script =
    document.currentScript ||
    document.querySelector(
      "script[data-h6-animation-loader]"
    );

  if (!script) {
    console.warn(
      "[H6 Loader] 找不到 loader script"
    );
    return;
  }

  const config = {
    gsap:
      script.dataset.gsapSrc,

    scrollTrigger:
      script.dataset.scrollTriggerSrc,

    children:
      script.dataset.childrenSrc,

    backgroundBlock:
      script.dataset.backgroundBlockSrc,

    rootMargin:
      script.dataset.rootMargin ||
      "500px 0px 500px 0px"
  };

  const promises =
    new Map();

  const observed =
    new WeakSet();

  const BACKGROUND_MODULE_SELECTOR =
    [
      "[data-h6-scroll-scene]",
      "[data-scroll-scene]",
      "[data-h6-scroll-background]",
      "[data-scroll-background]",
      "[data-h6-scroll-block]",
      "[data-scroll-block]",
      "[data-scroll-text]"
    ].join(",");

  const VIDEO_SCRUB_SELECTOR =
    "[data-h6-video-scrub]";

  const ALL_SELECTOR =
    [
      "[data-apple-scroll-reveal]",
      BACKGROUND_MODULE_SELECTOR,
      VIDEO_SCRUB_SELECTOR
    ].join(",");

  /* ============================================================
     SCRIPT LOADER
  ============================================================ */

  function isUrlLoaded(url) {
    return Array
      .from(
        document.scripts
      )
      .some(
        function (item) {
          return (
            item.src === url
          );
        }
      );
  }

  function loadScript(
    url,
    readyCheck
  ) {
    if (
      typeof readyCheck ===
        "function" &&
      readyCheck()
    ) {
      return Promise.resolve();
    }

    if (!url) {
      return Promise.reject(
        new Error(
          "[H6 Loader] 缺少 JS URL"
        )
      );
    }

    if (
      promises.has(url)
    ) {
      return promises.get(url);
    }

    const promise =
      new Promise(
        function (
          resolve,
          reject
        ) {
          if (
            isUrlLoaded(url)
          ) {
            let attempts = 0;

            const timer =
              setInterval(
                function () {
                  attempts += 1;

                  if (
                    !readyCheck ||
                    readyCheck()
                  ) {
                    clearInterval(
                      timer
                    );

                    resolve();

                    return;
                  }

                  if (
                    attempts >= 100
                  ) {
                    clearInterval(
                      timer
                    );

                    reject(
                      new Error(
                        "[H6 Loader] 等待已有 script 初始化超时：" +
                        url
                      )
                    );
                  }
                },
                50
              );

            return;
          }

          const element =
            document.createElement(
              "script"
            );

          element.src =
            url;

          element.async =
            true;

          element.onload =
            function () {
              requestAnimationFrame(
                function () {
                  if (
                    readyCheck &&
                    !readyCheck()
                  ) {
                    reject(
                      new Error(
                        "[H6 Loader] 文件已加载，但模块未注册：" +
                        url
                      )
                    );

                    return;
                  }

                  resolve();
                }
              );
            };

          element.onerror =
            function () {
              reject(
                new Error(
                  "[H6 Loader] 加载失败：" +
                  url
                )
              );
            };

          document.head.appendChild(
            element
          );
        }
      );

    promises.set(
      url,
      promise
    );

    return promise;
  }

  /* ============================================================
     CORE
  ============================================================ */

  async function ensureCore() {
    if (!window.gsap) {
      await loadScript(
        config.gsap,
        function () {
          return !!window.gsap;
        }
      );
    }

    if (!window.ScrollTrigger) {
      await loadScript(
        config.scrollTrigger,
        function () {
          return !!window.ScrollTrigger;
        }
      );
    }

    if (
      window.gsap &&
      window.ScrollTrigger
    ) {
      window.gsap.registerPlugin(
        window.ScrollTrigger
      );
    }
  }

  /* ============================================================
     CHILDREN MODULE
  ============================================================ */

  async function ensureChildrenModule() {
    await ensureCore();

    if (
      !window.H6ScrollChildrenAnimation
    ) {
      await loadScript(
        config.children,
        function () {
          return !!window
            .H6ScrollChildrenAnimation;
        }
      );
    }

    return window
      .H6ScrollChildrenAnimation;
  }

  /* ============================================================
     BACKGROUND / BLOCK MODULE
  ============================================================ */

  function getBackgroundApi() {
    return (
      window.H6BackgroundBlockScroll ||
      window.H6ScrollBackgroundBlock ||
      window.H6BackgroundTextScroll ||
      null
    );
  }

  async function ensureBackgroundModule() {
    await ensureCore();

    if (!getBackgroundApi()) {
      await loadScript(
        config.backgroundBlock,
        function () {
          return !!getBackgroundApi();
        }
      );
    }

    return getBackgroundApi();
  }

  async function loadChildren(
    element
  ) {
    try {
      const api =
        await ensureChildrenModule();

      if (
        api &&
        typeof api.init ===
          "function"
      ) {
        api.init(element);
      }
    }
    catch (error) {
      console.error(
        "[H6 Loader] Item 动画加载失败",
        error
      );
    }
  }

  async function loadBackgroundModule(
    element
  ) {
    try {
      const api =
        await ensureBackgroundModule();

      if (
        api &&
        typeof api.init ===
          "function"
      ) {
        api.init(element);
      }
    }
    catch (error) {
      console.error(
        "[H6 Loader] Background / Block 动画加载失败",
        error
      );
    }
  }

  /* ============================================================
     VIDEO SCRUB

     video-scrub.liquid 会先注册：
     window.H6VideoScrub = { init, destroy }

     Loader 只负责：
     1. 等元素接近 viewport
     2. 确保 GSAP / ScrollTrigger 已加载
     3. 调用 H6VideoScrub.init(element)
  ============================================================ */

  async function loadVideoScrub(
    element
  ) {
    try {
      await ensureCore();

      const api =
        window.H6VideoScrub;

      if (
        !api ||
        typeof api.init !==
          "function"
      ) {
        throw new Error(
          "[H6 Loader] H6VideoScrub 尚未注册"
        );
      }

      api.init(element);
    }
    catch (error) {
      console.error(
        "[H6 Loader] Video Scrub 加载失败",
        error
      );
    }
  }

  /* ============================================================
     LOAD BY ELEMENT
  ============================================================ */

  function loadForElement(
    element
  ) {
    if (!element) {
      return;
    }

    if (
      element.matches &&
      element.matches(
        "[data-apple-scroll-reveal]"
      )
    ) {
      loadChildren(
        element
      );
    }

    if (
      element.matches &&
      element.matches(
        BACKGROUND_MODULE_SELECTOR
      )
    ) {
      loadBackgroundModule(
        element
      );
    }

    if (
      element.matches &&
      element.matches(
        VIDEO_SCRUB_SELECTOR
      )
    ) {
      loadVideoScrub(
        element
      );
    }
  }

  /* ============================================================
     OBSERVER
  ============================================================ */

  const observer =
    "IntersectionObserver" in window
      ? new IntersectionObserver(
          function (
            entries
          ) {
            entries.forEach(
              function (
                entry
              ) {
                if (
                  !entry.isIntersecting
                ) {
                  return;
                }

                observer.unobserve(
                  entry.target
                );

                loadForElement(
                  entry.target
                );
              }
            );
          },
          {
            rootMargin:
              config.rootMargin,

            threshold:
              0
          }
        )
      : null;

  function observe(
    element
  ) {
    if (
      !element ||
      observed.has(element)
    ) {
      return;
    }

    observed.add(
      element
    );

    if (observer) {
      observer.observe(
        element
      );
    }
    else {
      loadForElement(
        element
      );
    }
  }

  /* ============================================================
     SCAN
  ============================================================ */

  function scan(root) {
    root =
      root ||
      document;

    if (
      root.matches &&
      root.matches(
        ALL_SELECTOR
      )
    ) {
      observe(root);
    }

    if (
      root.querySelectorAll
    ) {
      root
        .querySelectorAll(
          ALL_SELECTOR
        )
        .forEach(
          observe
        );
    }
  }

  /* ============================================================
     DESTROY
  ============================================================ */

  function destroy(root) {
    if (
      window.H6ScrollChildrenAnimation
    ) {
      window
        .H6ScrollChildrenAnimation
        .destroy(root);
    }

    const backgroundApi =
      getBackgroundApi();

    if (
      backgroundApi &&
      typeof backgroundApi.destroy ===
        "function"
    ) {
      backgroundApi.destroy(root);
    }

    if (
      window.H6VideoScrub &&
      typeof window.H6VideoScrub.destroy ===
        "function"
    ) {
      window.H6VideoScrub.destroy(root);
    }
  }

  /* ============================================================
     START
  ============================================================ */

  if (
    document.readyState ===
    "loading"
  ) {
    document.addEventListener(
      "DOMContentLoaded",
      function () {
        scan(document);
      },
      {
        once: true
      }
    );
  }
  else {
    scan(document);
  }

  /* ============================================================
     SHOPIFY
  ============================================================ */

  document.addEventListener(
    "shopify:section:load",
    function (event) {
      scan(
        event.target
      );
    }
  );

  document.addEventListener(
    "shopify:section:unload",
    function (event) {
      destroy(
        event.target
      );
    }
  );

  document.addEventListener(
    "shopify:block:select",
    function (event) {
      const element =
        event.target.closest
          ? event.target.closest(
              ALL_SELECTOR
            )
          : null;

      if (element) {
        loadForElement(
          element
        );
      }
    }
  );

  /* ============================================================
     API
  ============================================================ */

  window.H6AnimationLoader = {
    scan,
    loadForElement,

    ensureCore,
    ensureChildrenModule,
    ensureBackgroundModule,
    loadVideoScrub,

    destroy
  };
})();
